import React, { useState, useCallback, useRef } from "react";
import "./affix-input.scss";

export interface AffixInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  label?: string;
  labelAddon?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  hint?: string;
  error?: string;
  requiredError?: string;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  labelClassName?: string;
  inputClassName?: string;
}

let idCounter = 0;

export function AffixInput({
  label,
  labelAddon,
  prefix,
  suffix,
  hint,
  error,
  requiredError = "This field is required",
  size = "md",
  fullWidth = false,
  labelClassName,
  inputClassName,
  className,
  disabled,
  required,
  id,
  ...rest
}: AffixInputProps) {
  const [fallbackId] = useState(() => `affix-input-${++idCounter}`);
  const inputId = id || fallbackId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const cls = "affix-input";

  const value = rest.value ?? rest.defaultValue ?? "";
  const isEmpty = typeof value === "string" ? value.trim() === "" : !value;
  const requiredValidationError =
    required && touched && isEmpty ? requiredError : undefined;
  const resolvedError = error || requiredValidationError;
  const hasError = !!resolvedError;

  const rootClass = [
    cls,
    `${cls}--${size}`,
    fullWidth ? `${cls}--full-width` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const fieldClass = [
    `${cls}__field`,
    focused ? `${cls}__field--focused` : "",
    hasError ? `${cls}__field--error` : "",
    disabled ? `${cls}__field--disabled` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      rest.onFocus?.(e);
    },
    [rest.onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (required) setTouched(true);
      rest.onBlur?.(e);
    },
    [rest.onBlur, required]
  );

  const handleFieldClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  const message = hasError ? resolvedError : hint;
  const messageClass = hasError ? `${cls}__error` : `${cls}__hint`;

  return (
    <div className={rootClass}>
      {(label != null || labelAddon != null || required) && (
        <div className={`${cls}__label-row`}>
          {label != null && (
            <label
              htmlFor={inputId}
              className={[`${cls}__label`, labelClassName]
                .filter(Boolean)
                .join(" ")}
            >
              {label}
              {required && (
                <span className={`${cls}__required`} aria-hidden="true">
                  {" "}
                  *
                </span>
              )}
            </label>
          )}
          {labelAddon != null && (
            <span className={`${cls}__label-addon`}>{labelAddon}</span>
          )}
        </div>
      )}

      <div className={fieldClass} onClick={handleFieldClick}>
        {prefix != null && (
          <span className={`${cls}__prefix`}>{prefix}</span>
        )}
        <input
          ref={inputRef}
          id={inputId}
          className={[`${cls}__input`, inputClassName]
            .filter(Boolean)
            .join(" ")}
          disabled={disabled}
          required={required}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...rest}
        />
        {suffix != null && (
          <span className={`${cls}__suffix`}>{suffix}</span>
        )}
      </div>

      {message != null && <span className={messageClass}>{message}</span>}
    </div>
  );
}

export default AffixInput;
